"use client";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserCheck, Fingerprint, CalendarOff, Wallet, FolderOpen, ClipboardList } from "lucide-react";
import StatsCards from "./StatsCards";
import RevenueChart from "./RevenueChart";
import ProjectStatus from "./ProjectStatus";
import Announcements from "./Announcements";
import ClientProjectTracker from "./ClientProjectTracker";
import { useEmployee } from "../context/EmployeeContext";
import { useAuth } from "../context/AuthContext";

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good Morning";
  if (hour < 17) return "Good Afternoon";
  return "Good Evening";
};

const quickActionItems = [
  { icon: UserCheck, label: "Employees", route: "/Employee", color: "from-[#5D87FF] to-[#49BEFF]" },
  { icon: Fingerprint, label: "Attendance", route: "/Attendence", color: "from-[#EE6826] to-[#F49960]" },
  { icon: CalendarOff, label: "Leaves", route: "/leaves", color: "from-[#D02465] to-[#A14D88]" },
  { icon: Wallet, label: "Payroll", route: "/payrolls", color: "from-[#0D9E6A] to-[#A4D78F]" },
  { icon: FolderOpen, label: "Projects", route: "/Project", color: "from-[#7C3AED] to-[#C084FC]" },
  { icon: ClipboardList, label: "Tasks", route: "/task", color: "from-[#0EA5E9] to-[#67E8F9]" },
];

export default function Dashboard() {
  const { getEmployeeById } = useEmployee();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);

  const quickActions = useMemo(() => {
    const permissions = user?.permissions || [];
    return quickActionItems.filter(
      (item) =>
        permissions.includes(item.label) ||
        permissions.includes(item.route.replace("/", "")) ||
        permissions.includes(item.label.replace(/\s/g, ""))
    );
  }, [user]);

  useEffect(() => {
    const fetchEmployee = async () => {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user?.id || null;
      if (!userId) return;

      const data = await getEmployeeById(userId);
      setEmployee(data);
    };

    fetchEmployee();
  }, [getEmployeeById]);

  return (
    <div className="box-border px-9 py-5 m-0 max-sm:p-5">
      {/* Welcome Banner */}
      <div
        className="relative overflow-hidden rounded-2xl px-6 sm:px-8 py-6 sm:py-7 mb-8 text-white shadow-md"
        style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
      >
        <div className="relative z-10">
          <h1 className="text-2xl sm:text-3xl font-bold">
            {getGreeting()}{employee?.first_name ? `, ${employee.first_name}` : ""} 👋
          </h1>
          <p className="text-sm sm:text-base text-white text-opacity-80 mt-1.5">
            {new Date().toLocaleDateString(undefined, {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
            {" — "}Here's what's happening with your organization today.
          </p>
        </div>

        {/* Decorative circles */}
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white opacity-10" aria-hidden="true" />
        <div className="absolute right-16 -bottom-16 w-32 h-32 rounded-full bg-white opacity-10" aria-hidden="true" />
      </div>

      {/* Quick Actions */}
      {quickActions.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Quick Actions
          </h2>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
            {quickActions.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.label}
                  onClick={() => navigate(action.route)}
                  className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                >
                  <div className={`flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br ${action.color}`}>
                    <Icon size={20} className="text-white" />
                  </div>
                  <span className="text-xs font-medium text-gray-700 text-center">{action.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <StatsCards />
      {/* <RevenueChart /> */}

      <div className="box-border flex gap-6 p-0 m-0 mb-6 max-md:flex-col max-md:gap-5">
        <div className="w-[70%] max-md:w-full">
          <ProjectStatus />
        </div>
        <div className="w-[30%] max-md:w-full">
          <Announcements />
        </div>
      </div>

      <ClientProjectTracker />
    </div>
  );
}
